import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { inferRouterInputs } from '@trpc/server';
import type { AppRouter } from '@/types/appRouter';
import UserLayout from '@/components/Layout/UserLayout';
import { ChatWindow } from '@/components/AgentChat/ChatWindow';
import { ChatSessionSidebar } from '@/components/AgentChat/ChatSessionSidebar';
import { AutopilotPanel } from '@/components/AgentChat/AutopilotPanel';
import { extractAgentMode, extractAutopilotPlanId } from '@/components/AgentChat/agentSessionContext';
import { trpc } from '@/api/trpc';
import { useTrpcMutation } from '@/api/useTrpcMutation';
import { showToast } from '@/components/Layout/Toast';
import { useAuth } from '@context/AuthContext';
import SupportRestrictedFeatureCard from '@/components/Support/SupportRestrictedFeatureCard';
import { getDevelopmentCutoffDateTime } from '@einhaus/project314-shared';

type RouterInputs = inferRouterInputs<AppRouter>;
type StrategyAutopilotBlueprint = RouterInputs['agent']['autopilot']['validateBlueprint']['blueprint'];

type ChatMode = 'home' | 'new' | 'session';

const LAST_SESSION_STORAGE_KEY = 'chatLastSessionId';
const SIDEBAR_VISIBILITY_STORAGE_KEY_PREFIX = 'chatSidebarVisible:';

function parseSessionIdParam(raw: string | undefined): number | null {
    if (!raw) return null;
    const parsed = Number(raw);
    if (!Number.isFinite(parsed) || parsed <= 0) return null;
    return Math.floor(parsed);
}

function formatEtNow(): string {
    const now = new Date();

    const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: 'America/New_York',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
    }).formatToParts(now);

    const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '';

    return `${get('year')}-${get('month')}-${get('day')} ${get('hour')}:${get('minute')}:${get('second')}`;
}

function buildDefaultBlueprint(params: { userId: number }): StrategyAutopilotBlueprint {
    const createdAtEt = formatEtNow();

    return {
        blueprintVersion: 2,
        meta: {
            name: `Strategy Autopilot Draft (${createdAtEt})`,
            createdByUserId: params.userId,
            createdAtEt
        },
        interaction: {
            modeAfterLock: 'autonomous',
            clarificationPolicy: 'ask_only_if_blocked',
            progressCadence: { afterStep: true, heartbeatSeconds: 30 }
        },
        objective: {
            primaryMetric: 'totalProfitPercent',
            constraints: { minTrades: 50 },
            tieBreakers: ['stability', 'tradeCount']
        },
        scope: {
            instrument: 'equity',
            entityTypes: ['Asset'],
            barDuration: '1d',
            afterHoursRemoved: true
        },
        universe: {
            type: 'symbols',
            symbols: ['SPY']
        },
        strategyDesign: {
            strategyNameTemplate: 'AI Autopilot {DATE} \u2014 SPY \u2014 1D \u2014 Equity',
            legs: [{ kind: 'equity', side: 'long', quantityMode: 'default' }],
            entry: {
                source: 'create_new',
                template: 'sma_cross',
                params: { fast: 10, slow: 50 },
                variationPlan: { enabled: false, maxVariations: 0, method: 'grid' }
            },
            exit: {
                source: 'create_new',
                template: 'time_stop',
                params: { maxBarsHeld: 20 },
                variationPlan: { enabled: false, maxVariationsApplied: 0, generationMethod: 'template_sweep' }
            }
        },
        experimentPlan: {
            dateRange: {
                startEt: '2020-08-03 00:00:00',
                endEt: getDevelopmentCutoffDateTime()
            },
            includeOptions: false,
            horizonsSeconds: [2_592_000],
            baseline: {
                run: true,
                parameterDiscovery: { enabled: true, analyses: ['top_ranked_parameters'] }
            },
            screenerOptimization: {
                enabled: false,
                target: 'entry_gating',
                maxOptimizerJobs: 3,
                promote: { kind: 'best_single', topKToConsider: 25 },
                rerunExperimentAfterPromotion: true,
                maxPromotionIterations: 2
            },
            scorecard: {
                enabled: false,
                ruleBudget: { min: 2, max: 6 },
                mode: 'hard_veto',
                buildFrom: 'param_discovery',
                validateWith: { kind: 'ab_experiment', maxRuns: 4 }
            },
            exitOptimization: {
                enabled: false,
                validateWithExperiment: true,
                maxIterations: 2
            }
        },
        backtestPlan: {
            populateStrategyBacktests: {
                enabled: false,
                requiredBeforePortfolio: true
            },
            portfolio: {
                enabled: false,
                paper: { depositUsd: 100_000 },
                tradingProfile: { source: 'create_from_template', templateKey: 'default' },
                subscription: { ensureSubscribed: true },
                portfolioBacktest: { run: false }
            }
        },
        budgets: {
            maxWallClockMinutes: 120,
            maxTotalJobs: 40,
            maxExperiments: 10,
            maxScreenerOptimizerJobs: 5,
            maxPortfolioBacktests: 3,
            maxWriteActions: 250
        },
        stopConditions: {
            plateau: { enabled: true, minImprovement: 0.01, windowRuns: 3 },
            hardFailures: { maxConsecutiveStepFailures: 3 }
        },
        consent: {
            allowCreates: true,
            allowUpdates: true,
            allowDeletes: false,
            scopeTags: ['strategy', 'trade_logic_v2', 'experiments']
        },
        delivery: {
            finishLine: 'research_recommendation',
            optimizationPlaybook: 'fast',
            gatingPreference: 'none',
            portfolioOptimization: 'none'
        }
    };
}

export default function ChatPage() {
    const params = useParams<{ sessionId?: string }>();
    const location = useLocation();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { user, sessionPolicy } = useAuth();

    const mode: ChatMode = useMemo(() => {
        if (location.pathname === '/user/chat/new') return 'new';
        if (params.sessionId) return 'session';
        return 'home';
    }, [location.pathname, params.sessionId]);

    const sessionId = useMemo(() => (mode === 'session' ? parseSessionIdParam(params.sessionId) : null), [mode, params.sessionId]);

    const sessionsQuery = useQuery({
        ...trpc.agent.listSessionsEnriched.queryOptions({ limit: 50 }),
        enabled: sessionPolicy.canUseAgentChat,
        retry: false
    });

    const sessions = useMemo(() => sessionsQuery.data?.sessions ?? [], [sessionsQuery.data?.sessions]);

    const activeSession = useMemo(() => {
        if (!sessionId) return null;
        return sessions.find((s) => s.id === sessionId) ?? null;
    }, [sessionId, sessions]);

    const activeSessionAgentMode = useMemo(() => extractAgentMode(activeSession?.initialContextJson), [activeSession?.initialContextJson]);
    const isAutopilotExecSession = activeSessionAgentMode === 'strategy_autopilot_exec';

    const [isSidebarVisible, setIsSidebarVisible] = useState(true);

    useEffect(() => {
        if (!sessionId) {
            setIsSidebarVisible(true);
            return;
        }

        const key = `${SIDEBAR_VISIBILITY_STORAGE_KEY_PREFIX}${sessionId}`;
        const stored = sessionStorage.getItem(key);

        if (stored === '1') {
            setIsSidebarVisible(true);
            return;
        }

        if (stored === '0') {
            setIsSidebarVisible(false);
            return;
        }

        setIsSidebarVisible(!isAutopilotExecSession);
    }, [sessionId, isAutopilotExecSession]);

    const toggleSidebar = useCallback(() => {
        if (!sessionId) return;
        const key = `${SIDEBAR_VISIBILITY_STORAGE_KEY_PREFIX}${sessionId}`;

        setIsSidebarVisible((prev) => {
            const next = !prev;
            sessionStorage.setItem(key, next ? '1' : '0');
            return next;
        });
    }, [sessionId]);

    // Auto-detect autopilot context for current session
    const autopilotContextQuery = useQuery({
        ...trpc.agent.autopilot.getContextBySessionId.queryOptions({ sessionId: sessionId ?? 0 }),
        enabled: sessionPolicy.canUseAgentChat && sessionId !== null,
        refetchInterval: (query) => {
            const { data } = query.state;
            if (!data?.success || !data.hasAutopilot) return false;
            const status = data.run?.status ?? null;
            if (status === 'running' || status === 'paused') return 5000;
            return false;
        },
        retry: false
    });

    const autopilotPlanId = useMemo(() => {
        if (!autopilotContextQuery.data?.success || !autopilotContextQuery.data.hasAutopilot) return null;
        return autopilotContextQuery.data.plan?.id ?? null;
    }, [autopilotContextQuery.data]);

    // Also check if this session is a plan builder (has autopilotPlanId in enriched sessions)
    const sessionAutopilotPlanId = useMemo(() => {
        if (!sessionId) return null;
        const session = sessions.find((s) => s.id === sessionId);
        if (!session) return null;
        return extractAutopilotPlanId({ autopilotPlanId: session.autopilotPlanId, initialContextJson: session.initialContextJson });
    }, [sessionId, sessions]);

    // Use either autopilot context planId (from run) or enriched session planId (from builder)
    const selectedPlanId = mode === 'home' ? parseSessionIdParam(new URLSearchParams(location.search).get('planId') ?? undefined) : null;
    const activePlanId = autopilotPlanId ?? sessionAutopilotPlanId ?? selectedPlanId;

    const autopilotRunData = useMemo(() => {
        if (!autopilotContextQuery.data?.success || !autopilotContextQuery.data.hasAutopilot) return null;
        return autopilotContextQuery.data.run ?? null;
    }, [autopilotContextQuery.data]);

    const isAutopilotRunActive = autopilotRunData?.status === 'running' || autopilotRunData?.status === 'paused';

    const hasAutopilotPanel = activePlanId !== null;

    const setSessionId = useCallback(
        (id: number | null) => {
            if (id) {
                sessionStorage.setItem(LAST_SESSION_STORAGE_KEY, String(id));
                void navigate(`/user/chat/${id}`, { replace: true });
            } else {
                void navigate('/user/chat/new', { replace: true });
            }
        },
        [navigate]
    );

    const handleNewChat = useCallback(() => {
        void navigate('/user/chat/new');
    }, [navigate]);

    const handleSelectSession = useCallback(
        (id: number) => {
            sessionStorage.setItem(LAST_SESSION_STORAGE_KEY, String(id));
            void navigate(`/user/chat/${id}`);
        },
        [navigate]
    );

    const handleResumeLastChat = useCallback(() => {
        const lastId = sessionStorage.getItem(LAST_SESSION_STORAGE_KEY);
        const parsedId = lastId ? Number(lastId) : null;
        const validId = parsedId && sessions.some((s) => s.id === parsedId) ? parsedId : null;

        if (validId) {
            void navigate(`/user/chat/${validId}`);
        } else if (sessions.length > 0) {
            void navigate(`/user/chat/${sessions[0].id}`);
        }
    }, [navigate, sessions]);

    const hasLastSession = useMemo(() => {
        const lastId = sessionStorage.getItem(LAST_SESSION_STORAGE_KEY);
        return Boolean(lastId) || sessions.length > 0;
    }, [sessions.length]);

    // Create plan draft + start plan builder chat in one action
    const createPlanDraftMutation = useTrpcMutation(trpc.agent.autopilot.createPlanDraft.mutationOptions({}), {
        fallbackErrorMessage: 'Failed to create autopilot plan'
    });

    const startPlanBuilderChatMutation = useTrpcMutation(trpc.agent.autopilot.startPlanBuilderChat.mutationOptions({}), {
        fallbackErrorMessage: 'Failed to start plan builder chat'
    });

    const handleNewAutopilot = useCallback(async () => {
        if (!user?.id) {
            showToast('Not authenticated', 'error');
            return;
        }

        const blueprint = buildDefaultBlueprint({ userId: user.id });

        const draftResult = await createPlanDraftMutation.mutateAsync({ blueprint }).catch(() => null);
        if (!draftResult?.success) return;

        const chatResult = await startPlanBuilderChatMutation.mutateAsync({ planId: draftResult.planId }).catch(() => null);
        if (!chatResult?.success) return;

        showToast('Autopilot plan builder started', 'success');
        await queryClient.invalidateQueries(trpc.agent.listSessionsEnriched.pathFilter());
        await queryClient.invalidateQueries(trpc.agent.autopilot.listPlans.pathFilter());
        void navigate(`/user/chat/${chatResult.sessionId}`);
    }, [user, createPlanDraftMutation, startPlanBuilderChatMutation, queryClient, navigate]);

    // Handle clicking a plan in the sidebar
    const handleSelectPlan = useCallback(
        (planId: number) => {
            // Find a session linked to this plan
            const linkedSession = sessions.find((s) => {
                const sessionPlanId = extractAutopilotPlanId({
                    autopilotPlanId: s.autopilotPlanId,
                    initialContextJson: s.initialContextJson
                });

                return sessionPlanId === planId;
            });

            if (linkedSession) {
                handleSelectSession(linkedSession.id);
            } else {
                // MCP-created locked plans need a review/launch panel, not an editable builder chat.
                void queryClient
                    .fetchQuery(trpc.agent.autopilot.getPlan.queryOptions({ planId }))
                    .then(async (plan) => {
                        if (!plan.success) return;
                        if (plan.plan.status !== 'draft') {
                            void navigate(`/user/chat?planId=${planId}`);
                            return;
                        }
                        const res = await startPlanBuilderChatMutation.mutateAsync({ planId });
                        if (!res.success) return;
                        showToast('Plan builder chat started', 'success');
                        await queryClient.invalidateQueries(trpc.agent.listSessionsEnriched.pathFilter());
                        void navigate(`/user/chat/${res.sessionId}`);
                    })
                    .catch((error: unknown) =>
                        showToast(error instanceof Error ? error.message : 'Failed to open Autopilot plan', 'error')
                    );
            }
        },
        [sessions, handleSelectSession, startPlanBuilderChatMutation, queryClient, navigate]
    );

    if (!sessionPolicy.canUseAgentChat) {
        return (
            <UserLayout containerSize="2200px" title="AI Chat - Project 314">
                <SupportRestrictedFeatureCard
                    description="Agent chat is disabled during a read-only support session so admin activity cannot trigger autonomous or user-scoped write workflows."
                    title="AI Chat Is Unavailable"
                />
            </UserLayout>
        );
    }

    return (
        <UserLayout containerSize="2200px" title="AI Chat - Project 314">
            <div
                className={`grid grid-cols-1 gap-4 ${
                    hasAutopilotPanel
                        ? isSidebarVisible
                            ? 'lg:grid-cols-[320px_1fr_420px]'
                            : 'lg:grid-cols-[1fr_420px]'
                        : isSidebarVisible
                          ? 'lg:grid-cols-[320px_1fr]'
                          : 'lg:grid-cols-[1fr]'
                }`}
            >
                {isSidebarVisible && (
                    <ChatSessionSidebar
                        activeSessionId={sessionId}
                        isLoading={sessionsQuery.isLoading}
                        sessions={sessions}
                        onNewAutopilot={() => {
                            void handleNewAutopilot();
                        }}
                        onNewChat={handleNewChat}
                        onSelectPlan={handleSelectPlan}
                        onSelectSession={handleSelectSession}
                    />
                )}

                <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 overflow-hidden h-[calc(100vh-220px)] min-h-[520px]">
                    {mode === 'home' && (
                        <div className="flex flex-col items-center justify-center h-full gap-4 px-6 text-center">
                            <div className="text-2xl font-semibold text-white">AI Chat</div>
                            <p className="text-sm text-zinc-400 max-w-md">Start a new conversation or resume where you left off.</p>
                            <div className="flex gap-3">
                                <button
                                    className="px-4 py-2 rounded-md bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium"
                                    type="button"
                                    onClick={handleNewChat}
                                >
                                    New Chat
                                </button>
                                {hasLastSession && (
                                    <button
                                        className="px-4 py-2 rounded-md bg-zinc-700 hover:bg-zinc-600 text-white text-sm font-medium"
                                        type="button"
                                        onClick={handleResumeLastChat}
                                    >
                                        Resume Last Chat
                                    </button>
                                )}
                            </div>
                        </div>
                    )}

                    {mode === 'new' && <ChatWindow messagesLimit={800} sessionId={null} setSessionId={setSessionId} />}

                    {mode === 'session' && !sessionId && (
                        <div className="flex flex-col items-center justify-center h-full gap-4 px-6 text-center">
                            <div className="text-lg font-semibold text-white">Chat not found</div>
                            <p className="text-sm text-zinc-400 max-w-md">
                                This chat link is invalid or the chat is unavailable. Start a new conversation or return to the chat home.
                            </p>
                            <div className="flex gap-3">
                                <button
                                    className="px-4 py-2 rounded-md bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium"
                                    type="button"
                                    onClick={handleNewChat}
                                >
                                    New Chat
                                </button>
                                <button
                                    className="px-4 py-2 rounded-md bg-zinc-700 hover:bg-zinc-600 text-white text-sm font-medium"
                                    type="button"
                                    onClick={() => navigate('/user/chat')}
                                >
                                    Chat Home
                                </button>
                            </div>
                        </div>
                    )}

                    {mode === 'session' && sessionId && (
                        <ChatWindow
                            isSidebarVisible={isSidebarVisible}
                            key={sessionId}
                            messagesLimit={800}
                            pollSession={isAutopilotRunActive}
                            sessionId={sessionId}
                            setSessionId={setSessionId}
                            onToggleSidebar={toggleSidebar}
                        />
                    )}
                </div>

                {hasAutopilotPanel && activePlanId !== null && (
                    <AutopilotPanel
                        key={activePlanId}
                        planId={activePlanId}
                        runData={autopilotRunData}
                        onNavigateToSession={handleSelectSession}
                    />
                )}
            </div>
        </UserLayout>
    );
}
